import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import AsyncLoadingButton from "../../components/asyncLoadingButton/AsyncLoadingButton";
import DatePicker from "../../components/datePicker/DatePicker";
import LabelledCheckbox from "../../components/labelledCheckbox/LabelledCheckbox";
import NumberField from "../../components/numberField/NumberField";
import { getOrderPrice } from "../csvExport/getOrderPrice";
import type { SavedOrder } from "../orderTypes";
import { useCreateOrderConfirmation } from "./useCreateOrderConfirmation";
import { getAdvancePaymentDefaultDeadline } from "./getAdvancePaymentDefaultDeadline";

interface Props {
  readonly open: boolean;
  readonly order: Pick<SavedOrder, "id" | "currency" | "products" | "currencyExchangeRate" | "start" | "type">;
  onClose: () => void;
}

const OrderConfirmationDialog: FC<Props> = ({ open, order, onClose }) => {
  const [advanceAmount, setAdvanceAmount] = useState(() => {
    const totalPrice = getOrderPrice(order);
    const totalPriceValue = totalPrice.divideBy(10).round(-2).value;
    return typeof totalPriceValue === "number" ? totalPriceValue : 0;
  });
  const [advancePaymentDeadline, setAdvancePaymentDeadline] = useState(() => getAdvancePaymentDefaultDeadline(order));
  const [includeOptionPrice, setIncludeOptionPrice] = useState(false);
  const [includeAdvanceInfo, setIncludeAdvanceInfo] = useState(true);
  const [includeProductAddress, setIncludeProductAddress] = useState(false);
  const { createConfirmation } = useCreateOrderConfirmation(order);
  const submitEnabled = advanceAmount > 0 || !includeAdvanceInfo;
  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle>Potwierdzenie zamówienia</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column" }}>
        <LabelledCheckbox
          label="Pokaż informacje o zaliczce"
          checked={includeAdvanceInfo}
          onChange={setIncludeAdvanceInfo}
        />
        {includeAdvanceInfo && (
          <>
            <NumberField
              label="Kwota zaliczki"
              fullWidth
              margin="dense"
              autoFocus
              value={advanceAmount}
              onChange={(v) => setAdvanceAmount(v ?? 0)}
              suffix={order.currency}
            />
            <DatePicker
              label="Termin płatności zaliczki"
              margin="dense"
              fullWidth
              value={advancePaymentDeadline}
              onChange={setAdvancePaymentDeadline}
            />
          </>
        )}
        <LabelledCheckbox
          label="Pokaż ceny zasobów"
          disabled={order.type === "PACKAGE"}
          checked={includeOptionPrice}
          onChange={setIncludeOptionPrice}
        />
        <LabelledCheckbox
          label="Z adresami produktów"
          checked={includeProductAddress}
          onChange={setIncludeProductAddress}
        />
      </DialogContent>
      <DialogActions>
        <Button
          onClick={(e) => {
            e.stopPropagation();
            onClose();
          }}
        >
          Anuluj
        </Button>
        <AsyncLoadingButton
          disabled={!submitEnabled}
          onClick={async () => {
            await createConfirmation(
              includeAdvanceInfo
                ? {
                    type: "CUSTOMER",
                    includeOptionPrice,
                    includeProductAddress,
                    includeAdvanceInfo: true,
                    advanceAmount,
                    advancePaymentDeadline,
                  }
                : { type: "CUSTOMER", includeOptionPrice, includeProductAddress, includeAdvanceInfo: false },
            );
            onClose();
          }}
        >
          Generuj
        </AsyncLoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default OrderConfirmationDialog;
